/**
 * Reports: every stored report artifact of the case in one list, newest first.
 *
 * Report artifacts (`investigation_reports`) are stored per investigation and
 * the API lists them per investigation, so this reads each investigation's
 * list (a few at a time, capped) and merges them. The printable case report
 * (/cases/:id/report, built from the latest Auto-Investigate run) is linked at
 * the top. Opening an artifact writes its stored HTML into a new tab, the same
 * as the per-investigation list.
 *
 * R8: the effect owns a `cancelled` flag, so lists for a previous case never
 * land after navigation.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { FileText, Loader2, RefreshCw } from 'lucide-react';
import { investigationService } from '../../services/api';
import { buildReportFilename, reportTypeLabel, subjectFromMetadata, subjectIdentifier } from '../../utils/reportArtifacts';

export interface CaseReportsInvestigation { id: string; name: string }

interface ReportRow {
    id: string;
    investigationId: string;
    investigationName: string;
    title: string;
    report_type: string;
    created_at: string;
    size_bytes: number;
    metadata?: Record<string, unknown>;
}

/** Investigations read per refresh; the per-investigation list stays available on each card. */
export const MAX_INVESTIGATIONS_SCANNED = 25;
const CONCURRENCY = 4;

async function mapLimited<T, R>(items: T[], limit: number, fn: (item: T) => Promise<R>): Promise<R[]> {
    const out: R[] = new Array(items.length);
    let next = 0;
    const worker = async () => {
        while (next < items.length) {
            const i = next++;
            out[i] = await fn(items[i]);
        }
    };
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
    return out;
}

function download(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

export const CaseReportsSection: React.FC<{ caseId: string; investigations: CaseReportsInvestigation[] }> = ({ caseId, investigations }) => {
    const [rows, setRows] = useState<ReportRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [nonce, setNonce] = useState(0);
    const scanned = useMemo(() => investigations.slice(0, MAX_INVESTIGATIONS_SCANNED), [investigations]);
    const scanKey = scanned.map((i) => `${i.id}:${i.name}`).join('|');

    useEffect(() => {
        if (!caseId || scanned.length === 0) { setRows([]); setLoading(false); return; }
        let cancelled = false;
        setLoading(true);
        mapLimited(scanned, CONCURRENCY, async (inv) => {
            try {
                const list = await investigationService.listReportArtifacts(caseId, inv.id);
                return (Array.isArray(list) ? list : []).map((r: Record<string, unknown>) => ({
                    id: String(r.id),
                    investigationId: inv.id,
                    investigationName: inv.name,
                    title: String(r.title || 'Report'),
                    report_type: String(r.report_type || ''),
                    created_at: String(r.created_at || ''),
                    size_bytes: Number(r.size_bytes) || 0,
                    metadata: (r.metadata && typeof r.metadata === 'object') ? r.metadata as Record<string, unknown> : undefined,
                }));
            } catch {
                return [] as ReportRow[];
            }
        }).then((lists) => {
            if (cancelled) return;
            const merged = lists.flat().sort((a, b) => (Date.parse(b.created_at) || 0) - (Date.parse(a.created_at) || 0));
            setRows(merged);
            setLoading(false);
        });
        return () => { cancelled = true; };
        // scanKey stands in for `scanned` (a new array each render of the parent).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseId, scanKey, nonce]);

    const openReport = useCallback(async (r: ReportRow) => {
        try {
            const detail = await investigationService.getReportArtifact(caseId, r.investigationId, r.id);
            const win = window.open('', '_blank');
            if (!win) { toast.error('Pop-up blocked. Allow pop-ups to view the report.'); return; }
            win.document.write(detail?.html_content || '');
            win.document.close();
        } catch {
            toast.error('Failed to open the stored report');
        }
    }, [caseId]);

    const downloadReport = useCallback(async (r: ReportRow) => {
        try {
            const detail = await investigationService.getReportArtifact(caseId, r.investigationId, r.id);
            const subject = subjectFromMetadata(r.metadata || detail?.metadata);
            const safe = buildReportFilename(r.report_type || detail?.report_type || 'report', subject)
                || (r.title || 'report').replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '_').slice(0, 60);
            download(new Blob([detail?.html_content || ''], { type: 'text/html' }), `${safe || 'investigation-report'}.html`);
        } catch {
            toast.error('Failed to download the stored report');
        }
    }, [caseId]);

    return (
        <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="case-reports">
            <div className="flex items-center justify-between p-4 border-b border-border">
                <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" aria-hidden />
                    <h3 className="font-medium">Reports</h3>
                    <span className="px-2 py-0.5 text-xs bg-secondary rounded-full">{rows.length}</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <a
                        href={`/cases/${encodeURIComponent(caseId)}/report`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="case-reports-case-report"
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20"
                        title="The printable case report, built from the latest Auto-Investigate run"
                    >
                        <FileText className="w-3 h-3" aria-hidden /> Case report
                    </a>
                    <button
                        type="button"
                        onClick={() => setNonce((n) => n + 1)}
                        disabled={loading}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-secondary hover:bg-secondary/80 disabled:opacity-60"
                        aria-label="Refresh reports"
                    >
                        <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} aria-hidden /> Refresh
                    </button>
                </div>
            </div>
            {loading && rows.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> Loading reports…
                </div>
            ) : rows.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground" data-testid="case-reports-empty">
                    No stored reports for this case yet.
                </div>
            ) : (
                <ul className="divide-y divide-border max-h-80 overflow-y-auto">
                    {rows.map((r) => {
                        const subject = subjectIdentifier(subjectFromMetadata(r.metadata));
                        const created = new Date(r.created_at);
                        return (
                            <li key={`${r.investigationId}:${r.id}`} className="px-4 py-2.5 flex items-center gap-3" data-testid="case-report-row">
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm truncate" title={r.title}>{r.title}</p>
                                    <p className="text-[11px] text-muted-foreground flex flex-wrap gap-x-2 gap-y-0.5">
                                        <span className="font-medium text-foreground/70">{reportTypeLabel(r.report_type)}</span>
                                        {subject && <span className="font-mono">{subject}</span>}
                                        <span className="truncate" title={r.investigationName}>{r.investigationName}</span>
                                        <span>{Number.isNaN(created.getTime()) ? r.created_at : created.toLocaleString('en-IN')}</span>
                                        <span>{(r.size_bytes / 1024).toFixed(1)} KB</span>
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => { void openReport(r); }}
                                    className="text-xs px-2 py-1 rounded bg-primary/10 text-primary hover:bg-primary/20"
                                >
                                    Open
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { void downloadReport(r); }}
                                    className="text-xs px-2 py-1 rounded bg-secondary hover:bg-secondary/80"
                                >
                                    HTML
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
            {investigations.length > MAX_INVESTIGATIONS_SCANNED && (
                <p className="px-4 py-2 text-[11px] text-muted-foreground border-t border-border">
                    Showing reports from the first {MAX_INVESTIGATIONS_SCANNED} investigations listed above; open an investigation card for the rest.
                </p>
            )}
        </div>
    );
};

export default CaseReportsSection;
